/* eslint-disable quotes */
import { TabBar } from "@/components/tab-bar";
import { checkCurrentRouteAuth, checkUrl, getQueryParams } from "@/utils/checkUrl";
import { TaroCommonContextProvider } from "@/utils/context/basic";
import { setupGrayService } from "@/utils/gray";
import { SubAppEventName, useSubAppMessage } from "@/utils/hooks/useSubAppMessage";
import { resetRem } from "@/utils/rem";
import Track from "@/utils/track";
import { wxShare } from "@/utils/wx-share";
import { TrackDataContext } from "@akc/mx-taro-h5-components";
import { ProductAnimPicControl, TrackData } from "@akc/taro-service";
import microApp from "@micro-zoe/micro-app";
import React, { useCallback, useEffect, useRef, useState } from "react";
import KeepAlive, { autoFixContext, useActivate } from "react-activation";
import {
  Outlet,
  useLocation,
  useNavigate,
  useNavigationType,
  useParams,
  useSearchParams,
} from "react-router-dom";

// 路由名称枚举
export enum RouteName {
  Index = "Index",
  Activity = "Activity",
  Decorate = "Decorate",
  JoinBill = "JoinBill",
  SecKill = "SecKill",
  SecKill2 = "SecKill2",
  CouponList = "CouponList",
  TopList = "TopList",
  Logistics = "Logistics",
  MicroAppDetail = "MicroAppDetail", //微应用路由名称
  MicroAppDetail2 = "MicroAppDetail2",
  HighCommission = "HighCommission",
  Categroy = "Categroy",
  ShopInfo = "ShopInfo",
  ShowImages = "ShowImages",
  Market = "Market",
  MarketSearch = "MarketSearch",
  Focus = "Focus",
  ShopList = "ShopList",
  Promote = "Promote",
  RealShotList = "RealShotList",
  PromoteDetail = "PromoteDetail",
  RecommendList = "RecommendList",
  MaterialVideoList = "MaterialVideoList",
  MediaList = "MediaList",
  EnticeSearch = "EnticeSearch",
  VideoShare = "VideoShare",
  My = "My",
  MySetting = "MySetting",
  Personal = "Personal",
  PersonalSetting = "PersonalSetting",
  Home = "Home",
  VideoSharerInstruction = "VideoSharerInstruction",
  NewPerson = "NewPerson",
  Logout = "Logout",
  Material = "Material",
  PerformanceBoard = "PerformanceBoard",
  HistoryData = "HistoryData",
  ShareSetting = "ShareSetting",
  Search = "Search",
  SelectiveForward = "SelectiveForward",
  SelectivePosters = "SelectivePosters",
  DispatchTask = "DispatchTask",
  UserTrack = "UserTrack",
  RewardCenter = "RewardCenter",
  RewardCenterDetail = "RewardCenterDetail",
  RewardCenterDetailDistributor = "RewardCenterDetailDistributor",
  RewardCenterFillAddress = "RewardCenterFillAddress",
  RewardCenterLanding = "RewardCenterLanding",
  RewardSearch = "RewardSearch",
  FollowProduct = "FollowProduct",
  AftersaleApp = "aftersaleApp",
  CschatApp = "cschatApp",
  YXCalendar = "yxCalendar",
  YXInvite = "yxInvite",
  YXSignUp = "yxSignUp",
  YXHistory = "yxHistory",
  YXTravelConfirm = "yxTravelConfirm",
  HideSetting = "HideSetting",
  HideSettingSearch = "HideSettingSearch",
  SaleCalendar = "SaleCalendar",
  DailyReward = "DailyReward",
  MyReward = "MyReward",
  RewardOrder = "RewardOrder",
  CustomerReward = "CustomerReward",
  RedPacketDetail = "RedPacketDetail",

  ProductDetail = "ProductDetail",
  ProductDetailMaterial = "ProductDetailMaterial",
  ImageSearch = "ImageSearch",
  BrandBole = "BrandBole",
  GoldMedal = "GoldMedal",
  Achievement = "Achievement",
  YearEndBonusLanding = "YearEndBonusLanding",
  YearEndBonusSearch = "YearEndBonusSearch",
  YearEndBonusDetail = "YearEndBonusDetail",
  YearEndBonusRank = "YearEndBonusRank",
  Empowerment = "Empowerment",
  AfterPay = "AfterPay",

  BrandMaterial = "BrandMaterial",
  TradeSnapshotList = "TradeSnapshotList",
  TradeSnapshotDetail = "TradeSnapshotDetail",
  CommentList = "CommentList",
  JoinOrderNX = "JoinOrderNX", // 多买优惠，原 /#/goodsOfNpieceXfold

  RankScale = "RankScale",
  RankAll = "RankAll",
  QuestionsAnswers = "QuestionsAnswers", // 全部问答
  GiftPayStatus = "GiftPayStatus",
  GiftList = "GiftList",
  GiftNotice = "GiftNotice",
  ChangePhone = "ChangePhone",
  RewardGiftDetail = "RewardGiftDetail",
  RewardGiftActivity = "RewardGiftActivity",
  GoodsLivePreheat = "GoodsLivePreheat",
  SizeInfo = "SizeInfo", // 更多尺码参考
  CartShare = "CartShare",
  ShopAuthGuide = "ShopAuthGuide",
  NXCancel = "NXCancel",
  BestNewPeople = "BestNewPeople",
  SupperNavigator = "SupperNavigator",
  FullRank = "FullRank",
  WechatBind = "WechatBind",
  AssistantConfirmOrder = "AssistantConfirmOrder",
  RmsActivity = "RmsActivity",
  VideoCardList = "VideoCardList",
  OpenApp = "OpenApp",
  ShareCoupon = "ShareCoupon",
  InternetReport = "InternetReport",
}

// react-actication@0.8.0 版本配合 React 17+ 需要做如下调整以达修复对 Context 的破坏性影响
autoFixContext(
  [require("react/jsx-runtime"), "jsx", "jsxs", "jsxDEV"],
  [require("react/jsx-dev-runtime"), "jsx", "jsxs", "jsxDEV"],
);

export const TrackDataComp: React.FC<{ children?: React.ReactNode }> = props => {
  const [trackData, setTrackData] = useState<TrackData>({});

  useActivate(() => {
    // 激活时，刷入页面 trackData 缓存
    setTrackData(trackData);
  });
  return (
    <TrackDataContext.Provider
      value={{
        trackData,
        setTrackData,
      }}>
      {props.children}
    </TrackDataContext.Provider>
  );
};

export interface KeepAliveProps {
  element: React.ReactNode; //入口react节点
  name: string; // 缓存页面的key
  keepAliveList?: string[]; // 缓存页面的key,会从路由上去取
}

export const KeepAliveContainer: React.FC<KeepAliveProps> = (props: KeepAliveProps) => {
  const { element, name, keepAliveList } = props;
  const [search] = useSearchParams();
  const params = useParams();
  let cacheKey = name;
  if (keepAliveList && keepAliveList.length > 0) {
    cacheKey = keepAliveList.reduce((prev, cur) => {
      return prev + "-" + (search.get(cur) ?? params[cur]);
    }, name);
  }
  return (
    <KeepAlive cacheKey={cacheKey} saveScrollPosition="screen">
      <TrackDataComp>{element}</TrackDataComp>
    </KeepAlive>
  );
};

// 预定义的样式文件映射，确保Webpack可以静态分析
const styleMap: { [key: string]: () => Promise<any> } = {
  // 主要的懒加载页面样式映射
  "decorate/container": () => import("@/pages/decorate.module.less"),
  TopList: () => import("@/pages/TopList.module.less"),
  MarketSearch: () => import("@/pages/MarketSearch.module.less"),
  MarketContainer: () => import("@/pages/market.module.less"),
  Focus: () => import("@/pages/focus.module.less"),
  "promote/container": () => import("@/pages/promote/index.module.less"),
  "enticeSearch/searchContainer": () => import("@/pages/enticeSearch/search.module.less"),
  "my/index": () => import("@/pages/my/index.module.less"),
  "my-setting/index": () => import("@/pages/my-setting/index.module.less"),
  "material/container": () => import("@/pages/material/index.module.less"),
  "search/index": () => import("@/pages/search/index.module.less"),
  "daily-reward/index": () => import("@/pages/daily-reward/index.module.less"),
  "product-detail/normal": () => import("@/pages/product-detail/normal/index.module.less"),
  "product-detail/material": () => import("@/pages/product-detail/material/index.module.less"),
  "product-detail/size-info": () => import("@/pages/product-detail/size-info/index.module.less"),
  "shop-auth-guide/index": () => import("@/pages/shop-auth-guide/index.module.less"),
  "after-sales/NXCancel/index": () => import("@/pages/after-sales/NXCancel/index.module.less"),
  "year-end-bonus/best-new-people/index": () =>
    import("@/pages/year-end-bonus/best-new-people/index.module.less"),
  "year-end-bonus/supper-navigator/index": () =>
    import("@/pages/year-end-bonus/supper-navigator/index.module.less"),
  "year-end-bonus/full-rank/index": () =>
    import("@/pages/year-end-bonus/full-rank/index.module.less"),
  "customer-service/wechat-bind/index": () =>
    import("@/pages/customer-service/wechat-bind/index.module.less"),
  "open-app/index": () => import("@/pages/open-app/index.module.less"),
  "share-coupon/index": () => import("@/pages/share-coupon/index.module.less"),
  "send-gift/gift-pay-status/index": () =>
    import("@/pages/send-gift/gift-pay-status/index.module.less"),
  "send-gift/gift-list/index": () => import("@/pages/send-gift/gift-list/index.module.less"),
  "send-gift/gift-notice/index": () => import("@/pages/send-gift/gift-notice/index.module.less"),
  ShopInfo: () => import("@/pages/ShopInfo.module.less"),
  // 添加更多映射...
};

// 预加载CSS样式文件的函数
export const preloadStyles = async (lazyPath: string) => {
  const importFn = styleMap[lazyPath];
  if (importFn) {
    try {
      await importFn();
    } catch (e) {
      // 忽略样式文件不存在的错误，这在开发过程中很常见
      console.debug(`预加载样式文件失败或文件不存在: ${lazyPath}`, e);
    }
  }
};

// 增强版路由懒加载，支持预加载样式文件
export const lazyLoad = (LazyComponent, lazyPath?: string) => {
  // 如果提供了lazyPath，尝试预加载样式文件
  if (lazyPath) {
    // 在组件加载前预加载样式文件
    preloadStyles(lazyPath);
  }

  return (
    <React.Suspense
      fallback={
        <div
          style={{
            width: "100vw",
            height: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}></div>
      }>
      <LazyComponent />
    </React.Suspense>
  );
};

// Layout组件，一级路由视图组件
export const Layout: React.FC = () => {
  const [currentPath, setCurrentPath] = useState("");
  const firstLocationRef = useRef<string | null>(null);
  const location = useLocation();
  const track = Track();
  const navigater = useNavigate();

  const pageIdsRef = useRef<string[]>([]);

  // 查询全局灰度设置
  useEffect(() => {
    setupGrayService();
  }, []);

  const getBaseNavi = () => {
    return navigater;
  };

  const getWechatShare = () => {
    return wxShare;
  };

  const navigationType = useNavigationType();

  /**
   * 补充注册埋点通用属性
   * @param routePath 当前路由路径，如”/mx-shop-micro/home“
   */
  const handleTrackSuperProperty = useCallback(
    (routePath: string) => {
      let superProperty: Record<string, any> = {};
      // domain 字段,会场地址如https://m.imxxd.cn/mx-shop-micro?customPageCode=23061000000020001103994
      if (routePath === "/mx-shop-micro") {
        superProperty.domain = "CONFERENCE3";
      } else {
        superProperty.domain = "";
      }
      // 注册live_no字段
      // 场景1：从微应用跳过来（可能是在H5或者小程序环境），sessionStorage有值，但是当前应用未注册，则补充注册（注：这在本地不会生效，因为端口变了）
      // 场景2：在小程序中直接打开，webview地址带了sp_live_no，但是当前应用未注册，则补充注册
      const sp_live_no =
        sessionStorage.getItem("sp_live_no") ||
        (getQueryParams(window.location.href) || {}).sp_live_no;
      const crtSpLiveNo = track.getSuperProperty("live_no");
      if (sp_live_no && sp_live_no !== crtSpLiveNo) {
        console.log(
          "补充注册live_no",
          sessionStorage.getItem("sp_live_no"),
          (getQueryParams(window.location.href) || {}).sp_live_no,
          crtSpLiveNo,
        );
        // 补充注册到当前微应用的埋点通用属性
        superProperty.live_no = sp_live_no;
        // 同时保存一份到sessionStorage中,供主应用取用
        sessionStorage.setItem("sp_live_no", sp_live_no);
      }
      track.registerSuperProperty(superProperty);
    },
    [track],
  );

  React.useEffect(() => {
    // 页面切换时停止所有视频
    if (navigationType === "POP") {
      const lastPageId = pageIdsRef.current.pop();
      if (lastPageId) {
        ProductAnimPicControl.clear(lastPageId);
      }
    } else if (navigationType === "PUSH") {
      const pageId = `${location.pathname}-${location.hash}-${Date.now()}`;
      pageIdsRef.current.push(pageId);
      ProductAnimPicControl.setCurrentPageId(pageId);
    } else if (navigationType === "REPLACE") {
      const lastPageId = pageIdsRef.current.pop();
      if (lastPageId) {
        ProductAnimPicControl.clear(lastPageId);
      }
      const pageId = `${location.pathname}-${location.hash}-${Date.now()}`;
      pageIdsRef.current.push(pageId);
      ProductAnimPicControl.setCurrentPageId(pageId);
    }
  }, [location, navigationType]);

  useEffect(() => {
    if (firstLocationRef.current === null) {
      firstLocationRef.current = location.pathname || "/";
    }
    console.log(
      "firstLocationRef.current",
      firstLocationRef.current,
      "location",
      location.pathname,
    );
    const isFirstPageInH5Shop = !firstLocationRef.current.startsWith("/mx-shop-micro");
    if (isFirstPageInH5Shop && location.pathname.startsWith("/mx-shop-micro")) {
      // 第一个页面进的是h5shop页面, 后续进入主应用时，需要刷新下页面
      window.location.reload();
    }
    const href = decodeURIComponent(window.location.href.replace(/%/g, "%25"));
    if (href.indexOf("/mx-shop-micro") > -1) {
      setTimeout(() => {
        const tmp = decodeURIComponent(window.location.href.replace(/%/g, "%25"));
        if (tmp.indexOf("/mx-shop-micro") > -1 && tmp.endsWith("#/")) {
          const newUrl = tmp.split("#")[0];
          history.replaceState(null, document.title, newUrl);
        }
      }, 0);
    }

    setCurrentPath(location.pathname + location.hash);
    if (location.hash?.startsWith("#/index?")) {
      microApp.setData("h5shop", {
        type: "scroll-recover",
        target: "index",
        getBaseNavi,
        getWechatShare,
        resetRem,
      });
    }
    // 检查url中是否有shopId，没有则自动拼接shopId参数，重新进店
    checkUrl();
    // 检查当前路由是否需要认证
    checkCurrentRouteAuth();
  }, [location]);

  useSubAppMessage(SubAppEventName.nav, data => setCurrentPath(data));
  useSubAppMessage(SubAppEventName.switchRole, data => console.log("switchRole~~~~~~~~~~", data));

  //监听路由变化时是否需要设置overflow
  //单独给会场3.0设置下domain=CONFERENCE3
  useEffect(() => {
    if (
      location.pathname.includes("/mx-shop-micro/activity/") ||
      location.pathname === "/mx-shop-micro/decorate" ||
      location.pathname === "/mx-shop-micro/decorate/profit" ||
      location.pathname === "/mx-shop-micro/home" ||
      location.pathname === "/mx-shop-micro/daily-reward" ||
      location.pathname === "/mx-shop-micro/product-material" ||
      location.pathname === "/mx-shop-micro/product" ||
      location.pathname === "/mx-shop-micro/trade-snapshot/detail" ||
      location.pathname === "/mx-shop-micro/search" ||
      location.pathname === "/mx-shop-micro/rms-activity"
    ) {
      document.body.style.overflow = "unset";
    } else if (location.hash.includes("#/")) {
      document.body.style.overflow = "hidden auto";
    } else {
      document.body.style.overflow = "hidden";
    }
    handleTrackSuperProperty(location.pathname);
  }, [location]);

  return (
    <>
      <TaroCommonContextProvider>
        <main>
          <Outlet />
        </main>
        <TabBar currentPath={currentPath} />
      </TaroCommonContextProvider>
    </>
  );
};
